import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import {
  Building2,
  CheckCircle2,
  ArrowRight,
  Users,
  Clock,
  Shield,
  Target,
  TrendingUp,
} from "lucide-react";

export default function CorporateOutplacement() {
  const features = [
    {
      icon: Users,
      title: "Dedicated Account Management",
      description: "Your dedicated success manager ensures smooth onboarding and ongoing support for transitioning employees.",
    },
    {
      icon: Clock,
      title: "Rapid Deployment",
      description: "Get your team up and running in 24 hours with our streamlined enterprise onboarding process.",
    },
    {
      icon: TrendingUp,
      title: "Success Analytics",
      description: "Track placement rates, time-to-employment, and ROI with comprehensive reporting dashboards.",
    },
    {
      icon: Shield,
      title: "White-Glove Support",
      description: "Priority 24/7 support for your transitioning employees with dedicated success coaches.",
    },
  ];

  const benefits = [
    "Support employees with dignity and professionalism",
    "Reduce time-to-placement with AI-powered tools",
    "Demonstrate corporate social responsibility",
    "Protect employer brand during transitions",
    "Comprehensive analytics and reporting",
    "Custom onboarding and training programs",
    "Enterprise security and compliance",
    "Flexible contract terms and pricing",
  ];

  return (
    <>
      <Helmet>
        <title>Corporate Outplacement Services | LoopBaan - Employee Transition Support</title>
        <meta
          name="description"
          content="Support transitioning employees with LoopBaan's enterprise outplacement platform. Dedicated account management, rapid deployment, and proven results."
        />
      </Helmet>

      <MegaMenuNav />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
              <Building2 className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">Corporate Outplacement</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Support Employees
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                Through Career Transitions
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Provide world-class outplacement support with AI-powered career tools, dedicated coaching, and proven results. Protect your brand while helping employees land faster.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                  Request Proposal
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6">
                  View Enterprise Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Dedicated</div>
              <div className="text-gray-400 text-sm md:text-base">Account Manager</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Rapid</div>
              <div className="text-gray-400 text-sm md:text-base">24hr Deployment</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Analytics</div>
              <div className="text-gray-400 text-sm md:text-base">Success Tracking</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">24/7</div>
              <div className="text-gray-400 text-sm md:text-base">Priority Support</div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Enterprise-Grade Outplacement
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need to support transitioning employees with dignity
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700 p-8 hover:border-teal-500/50 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-500/10 rounded-lg">
                    <feature.icon className="h-8 w-8 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Why Companies Choose LoopBaan
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4">
                  <CheckCircle2 className="h-6 w-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/20 rounded-2xl p-12 text-center">
            <Target className="h-16 w-16 text-teal-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Support Your Team?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help your transitioning employees land faster with dignity.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                Schedule Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}
